import type { IAiSearchFilters } from './aiSearchToParams';

type TAiSearchIntent = 'fresh' | 'narrow' | 'replace_field' | 'replace_all';

/**
 * How to handle the renter's query. 'search' applies filters; 'question' is a
 * policy/how-it-works ask the box can't search for (the server attaches help
 * articles); 'handoff' is a request to reach a person. Absent = 'search'
 * (older agent builds didn't send it).
 */
type TAiSearchOutcome = 'search' | 'question' | 'handoff';

/** A help-center article the server suggests for a 'question' outcome. */
interface IAiHelpArticle {
  title: string;
  url: string;
  snippet?: string;
}

/**
 * Response from the RV AI-search extractor (via the /api/ai-search proxy).
 * Mirrors agents-platform rvToSearchResponseSchema.
 */
export interface IAiSearchResponse {
  filters: IAiSearchFilters;
  intent: TAiSearchIntent;
  confidence: Partial<Record<keyof IAiSearchFilters, 'high' | 'med' | 'low'>>;
  summary: string;
  replacedFields?: string[];
  /** Facet field names to clear from the current filters (e.g. ['instantBook']). */
  remove?: string[];
  outcome?: TAiSearchOutcome;
  /** Present only on a 'question' outcome: real help-center articles to suggest. */
  help?: { searchUrl: string; articles: IAiHelpArticle[] };
}

/**
 * Call the AI-search extractor. `currentFilters` is the renter's current
 * search (null on a fresh/homepage call) so the extractor can classify
 * intent (narrow vs replace) when refining.
 */
export async function postAiSearch(args: {
  query: string;
  currentFilters?: IAiSearchFilters | null;
  now: string;
  persistedAddress?: string;
  locale?: string;
  source?: string;
}): Promise<IAiSearchResponse> {
  const res = await fetch('/api/ai-search', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: args.query,
      currentFilters: args.currentFilters ?? null,
      source: args.source ?? 'rv_search',
      context: {
        now: args.now,
        persistedAddress: args.persistedAddress,
        locale: args.locale,
      },
    }),
  });
  if (!res.ok) throw new Error(`ai_search_${res.status}`);
  return (await res.json()) as IAiSearchResponse;
}
